/**
 * UploadProcessingView
 *
 * Displays real-time processing progress for uploaded documents.
 * Shown in the upload sheet after files finish uploading.
 * Reads file list from zustand store + subscribes to processing status via Convex.
 */

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useDocumentProcessingStatus } from "@/hooks/use-document-processing-status";
import {
  useChecklistSuggestions,
  type ResolvedSuggestion,
} from "@/hooks/use-checklist-suggestions";
import { useTranslation } from "@/packages/utils";
import { FileIcon } from "../FileIcon";
import {
  ProcessingStepList,
  type ProcessingStep,
  type ProcessingStepStatus,
} from "./ProcessingStepList";
import { ChecklistSuggestions } from "./ChecklistSuggestions";
import { UnmatchedDocumentSection } from "./UnmatchedDocumentSection";
import type { Id } from "@/packages/types";
import { useUploadSessionStore } from "@/stores/upload-session-store";

interface ProcessingFileCardProps {
  file: {
    localId: string;
    fileName: string;
    fileType: string;
    documentId?: Id<"documents">;
    uploadStatus: "pending" | "uploading" | "done" | "error";
  };
  processingStatus:
    | {
        summaryStatus?: ProcessingStepStatus;
        suggestedChecklistItemsStatus?: ProcessingStepStatus;
        isProcessing: boolean;
      }
    | undefined;
  hasAudit: boolean;
  auditId: Id<"audits"> | null;

  // Optional controlled props from parent (UploadSheet) for cross-document batch accept
  selectedIds?: Set<Id<"checklistItems">>;
  onSelectedIdsChange?: (
    documentId: Id<"documents">,
    ids: Set<Id<"checklistItems">>,
  ) => void;
  onSuggestionsReady?: (
    documentId: Id<"documents">,
    suggestions: ResolvedSuggestion[],
  ) => void;
  externalConfirmed?: {
    total: number;
    accepted: number;
    items: ResolvedSuggestion[];
  } | null;
  showConfirmButton?: boolean;
}

function isStillProcessing(
  status: ProcessingFileCardProps["processingStatus"],
  hasAudit: boolean,
): boolean {
  if (!status) return true;
  if (hasAudit) return status.isProcessing;
  // Without audit, the matching step is irrelevant — only check summary
  const summaryActive =
    status.summaryStatus === "pending" ||
    status.summaryStatus === "generating" ||
    status.summaryStatus === "retrying";
  return summaryActive;
}

function ProcessingFileCard({
  file,
  processingStatus,
  hasAudit,
  auditId,
  selectedIds,
  onSelectedIdsChange,
  onSuggestionsReady,
  externalConfirmed,
  showConfirmButton = true,
}: ProcessingFileCardProps) {
  const { t } = useTranslation(["documents"]);
  const { suggestions } = useChecklistSuggestions(file.documentId ?? null);
  const [suggestionsWereFound, setSuggestionsWereFound] = useState(false);

  // Track if suggestions were ever found (persists after backend clears them post-accept)
  useEffect(() => {
    if (suggestions.length > 0) {
      setSuggestionsWereFound(true);
    }
  }, [suggestions.length]);

  // Handle files that failed to upload
  if (file.uploadStatus === "error") {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
        <div className="flex items-center gap-3">
          <FileIcon type={file.fileType} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-primary truncate">{file.fileName}</p>
            <p className="text-xs text-red-400">
              {t("documents:upload.processing.uploadFailed")}
            </p>
          </div>
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
        </div>
      </div>
    );
  }

  const steps: ProcessingStep[] = [
    {
      label: t("documents:upload.processing.fileUploaded"),
      status: "completed",
    },
    {
      label: t("documents:upload.processing.summaryGenerated"),
      status: processingStatus?.summaryStatus ?? "pending",
      activeDescription: t("documents:upload.processing.summaryActiveHint"),
    },
    ...(hasAudit
      ? [
          {
            label: t("documents:upload.processing.findingMatches"),
            status:
              processingStatus?.suggestedChecklistItemsStatus ?? "pending",
            activeDescription: t(
              "documents:upload.processing.matchingActiveHint",
            ),
          },
        ]
      : []),
  ];

  const matchingCompleted =
    hasAudit && processingStatus?.suggestedChecklistItemsStatus === "completed";

  const showSuggestions =
    matchingCompleted && suggestionsWereFound && file.documentId;

  const showUnmatched =
    matchingCompleted &&
    !suggestionsWereFound &&
    suggestions.length === 0 &&
    file.documentId &&
    auditId;

  const hasFailedSteps =
    processingStatus?.summaryStatus === "failed" ||
    processingStatus?.suggestedChecklistItemsStatus === "failed";

  return (
    <div
      className={`rounded-lg border p-3 ${
        hasFailedSteps
          ? "border-red-500/30 bg-red-500/5"
          : "border-border bg-card"
      }`}
    >
      <div className="flex items-center gap-3">
        <FileIcon type={file.fileType} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-primary truncate">{file.fileName}</p>
          <p className="text-xs text-muted">{file.fileType}</p>
        </div>
        {hasFailedSteps ? (
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
        ) : !isStillProcessing(processingStatus, hasAudit) ? (
          <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
        ) : (
          <Loader2 size={16} className="text-blue-500 animate-spin shrink-0" />
        )}
      </div>
      <div className="mt-2">
        <ProcessingStepList steps={steps} />
      </div>
      {showSuggestions && file.documentId && (
        <div className="mt-3 pt-3 border-t border-border">
          <ChecklistSuggestions
            documentId={file.documentId}
            selectedIds={selectedIds}
            onSelectedIdsChange={(ids) => {
              if (file.documentId) onSelectedIdsChange?.(file.documentId, ids);
            }}
            showConfirmButton={showConfirmButton}
            externalConfirmed={
              file.documentId ? (externalConfirmed ?? null) : null
            }
            onSuggestionsReady={(sugs) => {
              if (file.documentId) onSuggestionsReady?.(file.documentId, sugs);
            }}
          />
        </div>
      )}
      {showUnmatched && file.documentId && auditId && (
        <div className="mt-3 pt-3 border-t border-border">
          <UnmatchedDocumentSection
            documentId={file.documentId}
            auditId={auditId}
          />
        </div>
      )}
    </div>
  );
}

interface UploadProcessingViewProps {
  selectedMap?: Record<string, Set<Id<"checklistItems">>>;
  onSelectedChange?: (
    documentId: Id<"documents">,
    ids: Set<Id<"checklistItems">>,
  ) => void;
  suggestionsMap?: Record<string, ResolvedSuggestion[]>;
  onSuggestionsReady?: (
    documentId: Id<"documents">,
    suggestions: ResolvedSuggestion[],
  ) => void;
  externalConfirmedMap?: Record<
    string,
    { total: number; accepted: number; items: ResolvedSuggestion[] }
  >;
  showConfirmButton?: boolean;
}

export function UploadProcessingView({
  selectedMap = {},
  onSelectedChange,
  onSuggestionsReady,
  externalConfirmedMap = {},
  showConfirmButton = true,
}: UploadProcessingViewProps = {}) {
  const { t } = useTranslation(["documents"]);
  const sessionFiles = useUploadSessionStore((s) => s.files);
  const selectedAuditId = useUploadSessionStore((s) => s.selectedAuditId);
  const hasAudit = selectedAuditId !== null;

  const documentIds = sessionFiles
    .filter(
      (f): f is typeof f & { documentId: Id<"documents"> } =>
        f.documentId !== undefined,
    )
    .map((f) => f.documentId);

  const { statuses } = useDocumentProcessingStatus(documentIds);

  const totalFiles = sessionFiles.length;
  const processedFiles = documentIds.filter((id) => {
    const s = statuses.get(id);
    return s && !isStillProcessing(s, hasAudit);
  }).length;

  const failedFiles = documentIds.filter((id) => {
    const s = statuses.get(id);
    return (
      s?.summaryStatus === "failed" ||
      (hasAudit && s?.suggestedChecklistItemsStatus === "failed")
    );
  }).length;

  const allProcessed = processedFiles === totalFiles && totalFiles > 0;

  // Track previous allProcessed state to detect transition
  const prevAllProcessedRef = useRef(false);
  // Capture the phase when component first mounts to avoid re-firing the toast
  // when the sheet is reopened after processing already completed
  const phaseOnMountRef = useRef<string | null>(null);
  useEffect(() => {
    if (phaseOnMountRef.current === null) {
      phaseOnMountRef.current = useUploadSessionStore.getState().phase;
    }

    if (allProcessed && !prevAllProcessedRef.current) {
      // Only fire toast when processing completes during this component lifecycle,
      // not when the component remounts with processing already done
      if (phaseOnMountRef.current !== "completed") {
        const filesWithSuggestions = documentIds.filter((id) => {
          const s = statuses.get(id);
          return s?.suggestedChecklistItemsStatus === "completed";
        });

        if (filesWithSuggestions.length > 0) {
          toast.success(
            t("documents:upload.processing.complete", {
              count: filesWithSuggestions.length,
            }),
            { richColors: true },
          );
        } else {
          toast.success(t("documents:upload.processing.allProcessed"), {
            richColors: true,
          });
        }
      }

      // Transition session phase to completed
      useUploadSessionStore.getState().setPhase("completed");
    }
    prevAllProcessedRef.current = allProcessed;
  }, [allProcessed, documentIds, statuses, t]);

  // Calculate overall progress percentage (per-file: upload=33%, summary=66%, matching=100%)
  const progressPercent = (() => {
    if (totalFiles === 0) return 0;
    let total = 0;
    for (const file of sessionFiles) {
      if (file.uploadStatus === "done") total += 33;
      if (file.documentId) {
        const s = statuses.get(file.documentId);
        if (s?.summaryStatus === "completed") total += 33;
        if (hasAudit && s?.suggestedChecklistItemsStatus === "completed")
          total += 34;
        else if (!hasAudit && s?.summaryStatus === "completed") total += 34;
      }
    }
    return Math.round(total / totalFiles);
  })();

  return (
    <motion.div
      className="flex flex-col gap-3"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Progress bar */}
      {!allProcessed && (
        <div className="h-1 w-full rounded-full bg-surface overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-accent"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      )}

      {/* Overall progress header */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-secondary">
          {allProcessed && failedFiles > 0
            ? t("documents:upload.processing.completedWithErrors", {
                count: failedFiles,
              })
            : allProcessed
              ? t("documents:upload.processing.allFilesProcessed")
              : t("documents:upload.processing.inProgress", {
                  count: totalFiles,
                })}
        </span>
        <AnimatePresence mode="wait">
          {!allProcessed && (
            <motion.div
              key="loading"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <Loader2 size={14} className="text-blue-500 animate-spin" />
            </motion.div>
          )}
          {allProcessed && failedFiles > 0 && (
            <motion.div
              key="error"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <AlertTriangle size={14} className="text-red-500" />
            </motion.div>
          )}
          {allProcessed && failedFiles === 0 && (
            <motion.div
              key="success"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <CheckCircle2 size={14} className="text-emerald-500" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Nero-Ki info banner — only while processing */}
      {!allProcessed && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="flex items-start gap-2.5 rounded-lg bg-accent/8 border border-accent/15 px-3.5 py-2.5"
        >
          <Sparkles size={15} className="text-accent shrink-0 mt-0.5" />
          <p className="text-[12.5px] leading-relaxed text-secondary">
            {t("documents:upload.processing.neroKiHint")}
          </p>
        </motion.div>
      )}

      {/* Per-file processing cards */}
      <div className="space-y-2">
        {sessionFiles.map((file, index) => (
          <motion.div
            key={file.localId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: index * 0.08 }}
          >
            <ProcessingFileCard
              file={file}
              processingStatus={
                file.documentId ? statuses.get(file.documentId) : undefined
              }
              hasAudit={hasAudit}
              auditId={selectedAuditId}
              selectedIds={
                file.documentId
                  ? selectedMap[String(file.documentId)]
                  : undefined
              }
              onSelectedIdsChange={(docId, ids) =>
                onSelectedChange?.(docId, ids)
              }
              onSuggestionsReady={(docId, sugs) =>
                onSuggestionsReady?.(docId, sugs)
              }
              externalConfirmed={
                file.documentId
                  ? (externalConfirmedMap[String(file.documentId)] ?? null)
                  : null
              }
              showConfirmButton={showConfirmButton}
            />
          </motion.div>
        ))}
      </div>

      {/* Skeleton preview cards — fill empty space while processing */}
      {!allProcessed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="space-y-2 mt-1"
        >
          {/* Summary skeleton */}
          <div className="rounded-lg border border-border/50 bg-surface/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-3 w-3 rounded-full bg-card-hover animate-pulse" />
              <div className="h-3 w-24 rounded bg-card-hover animate-pulse" />
            </div>
            <div className="space-y-1.5">
              <div className="h-2.5 w-full rounded bg-card-hover animate-pulse" />
              <div className="h-2.5 w-4/5 rounded bg-card-hover animate-pulse" />
              <div className="h-2.5 w-3/5 rounded bg-card-hover animate-pulse" />
            </div>
          </div>
          {/* Checklist matches skeleton */}
          {hasAudit && (
            <div className="rounded-lg border border-border/50 bg-surface/50 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-card-hover animate-pulse" />
                <div className="h-3 w-32 rounded bg-card-hover animate-pulse" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded bg-card-hover animate-pulse" />
                  <div className="h-2.5 w-3/4 rounded bg-card-hover animate-pulse" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded bg-card-hover animate-pulse" />
                  <div className="h-2.5 w-2/3 rounded bg-card-hover animate-pulse" />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}
